import React from 'react';

import ManagedFormGroup from 'components/form/managed_input/form_group';
import Options from 'components/form/managed_input/options';

import type { Value } from './types';

// First option is an explicitly selectable blank so users can clear the field after picking a
// value. ManagedSelectInput's built-in `includeBlank` placeholder is rendered as `disabled hidden`
// (see app/javascript/components/form/managed_input/select.jsx), which would prevent re-selection.
const OPTIONS = [
  { label: '', value: '' },
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

// Map stored booleans onto the dropdown's string values; null falls to blank.
function booleanToString(value: Value): string {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

// Convert the dropdown's string value back to a real boolean (or null for blank) before
// emitting up. Callers (Redux state, hidden inputs, etc.) see the typed value, which keeps
// the boolean value validator on the server side happy.
function stringToBoolean(value: Value): Value {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

interface Props {
  hasError?: boolean;
  label?: string;
  name?: string;
  required?: boolean;
  value: Value;
  onChange?: (changes: Record<string, Value>) => void;
  onValueChange?: (newValue: Value) => void;
}

export default function BooleanInput({
  hasError,
  label,
  name,
  required,
  value,
  onChange = () => { },
  onValueChange = () => { },
}: Props) {
  const handleChange = (changes: Record<string, Value>) => {
    const converted: Record<string, Value> = {};
    for (const key in changes) {
      converted[key] = stringToBoolean(changes[key]);
    }
    onChange(converted);
  };

  return (
    <ManagedFormGroup
      hasError={hasError}
      label={label}
      name={name}
      required={required}
      type="select"
      value={booleanToString(value)}
      onChange={handleChange}
      onValueChange={(newValue: Value) => onValueChange(stringToBoolean(newValue))}
    >
      <Options options={OPTIONS} />
    </ManagedFormGroup>
  );
}
